import { Icon } from '@teetsh/app/src/components/Icons/icon';
import { Attachment } from './attachment';
import { Color } from '@teetsh/app/src/types/colors';
import {
  CheckIcon,
  FolderIcon,
  HashtagIcon,
  PhoneIcon,
  TagIcon,
  Bars4Icon,
} from '@heroicons/react/24/outline';
import Tt from '@teetsh/app/src/components/Icons/icons/Tt';
import { nanoid } from 'nanoid';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';

export interface CustomFieldValue {
  id: string;
  value: any; // TODO better typing
  items?: CustomFieldItem[];
}

export interface CustomFieldItem {
  id: string;
  value: string;
}

export interface CustomFieldOption {
  value: string;
  id: string;
  color?: Color | TagColorWithIntensity;
  position?: number;
}

export interface CustomFieldDefinition {
  id: string;
  name: string;
  pluralName?: string;
  type: FieldType;
  options: CustomFieldOption[];
}

export type CustomFieldTarget =
  | 'cahierJournal'
  | 'headCahierJournal'
  | 'fdp'
  | 'fdpPhases'
  | 'fdpSummary'
  | 'sequence';

export interface CustomFieldDefinitionWithTargets
  extends CustomFieldDefinition {
  targets: CustomFieldTarget[];
}

export interface CustomFieldPreset {
  id: string;
  name: string;
  pluralName?: string;
  type: FieldType;
  options: CustomFieldOption[];
  value: any;
}

export type FieldValueType =
  | string
  | string[]
  | number
  | boolean
  | Date
  | Attachment[]
  | undefined;

const allAvailableFieldTypes = [
  'text',
  'number',
  'phone',
  'multiselect',
  'filesAndMedia',
  'bool',
  'select',
] as const;

export type FieldType = (typeof allAvailableFieldTypes)[number];

// these types can be returned on eleves by the API but are not available to the user yet
export type AdditionalFieldType = 'richtext' | 'date' | 'image';

export const CustomFieldDefinitionDefaults: {
  [K in FieldType]: {
    value: K;
    icon: Icon;
    hasPluralName: boolean;
    isEmpty: (field: CustomFieldValue) => boolean;
    genNewItem: (definition: CustomFieldDefinition) => CustomFieldValue;
  };
} = {
  text: {
    value: 'text',
    icon: Tt as any,
    hasPluralName: false,
    isEmpty: field => field.value === undefined || field.value === null,
    genNewItem: definition => ({
      id: definition.id,
      type: definition.type,
      items: [],
      value: '',
    }),
  },
  select: {
    value: 'select',
    icon: TagIcon,
    hasPluralName: true,
    isEmpty: field => field.value === undefined,
    genNewItem: definition => ({
      id: definition.id,
      type: definition.type,
      items: [],
      value: [],
    }),
  },
  multiselect: {
    value: 'multiselect',
    icon: Bars4Icon,
    hasPluralName: true,
    isEmpty: field => (field.items || []).length === 0,
    genNewItem: definition => ({
      id: definition.id,
      type: definition.type,
      value: '',
      items: [{ id: nanoid(5), value: '' }],
    }),
  },
  number: {
    value: 'number',
    icon: HashtagIcon,
    hasPluralName: false,
    isEmpty: field => field.value === undefined,
    genNewItem: definition => ({
      id: definition.id,
      type: definition.type,
      items: [],
      value: 0,
    }),
  },
  phone: {
    value: 'phone',
    icon: PhoneIcon,
    hasPluralName: false,
    isEmpty: field => field.value === undefined || field.value === null,
    genNewItem: definition => ({
      id: definition.id,
      type: definition.type,
      items: [],
      value: '',
    }),
  },
  filesAndMedia: {
    value: 'filesAndMedia',
    icon: FolderIcon,
    hasPluralName: true,
    isEmpty: field => (field.value || []).length === 0,
    genNewItem: definition => ({
      id: definition.id,
      type: definition.type,
      items: [],
      value: [],
    }),
  },
  bool: {
    value: 'bool',
    icon: CheckIcon,
    hasPluralName: false,
    isEmpty: field => field.value === undefined,
    genNewItem: definition => ({
      id: definition.id,
      type: definition.type,
      items: [],
      value: false,
    }),
  },
};
